/**
 * Чистые помощники для обработки ответов сервисов детекции
 * (детектор падений /admin/fall, детектор неподвижности /admin/acc-still)
 */

import { IFallSettings, IAccStillSettings } from '../../../models/types';

// С 2026-11-02 API трёхпольное: PUT без impactSpikeThreshold → 400 (missing required properties)
export const isFallSettings = (body: any): body is IFallSettings =>
  typeof body?.threshold === 'number' &&
  typeof body?.dedupWindowSeconds === 'number' &&
  typeof body?.impactSpikeThreshold === 'number';

// impactSpikeThreshold: 0 — детекция удара выключена (событие только по свободному падению,
// len <= 1 - threshold); включённый порог живёт в [1.1, 3], где 1.1 — максимальная чувствительность
export const IMPACT_SPIKE_DISABLED = 0;
export const IMPACT_SPIKE_MIN = 1.1;
export const IMPACT_SPIKE_MAX = 3;

export interface IFallFormValues extends IFallSettings {
  impactEnabled: boolean;
}

// API-тройка → состояние формы: включённость + значение слайдера.
// Значения вне [1.1, 3] подтягиваются к границам (сервер по спецификации их не отдаёт)
export const toFallFormValues = (settings: IFallSettings): IFallFormValues => {
  const enabled = settings.impactSpikeThreshold > IMPACT_SPIKE_DISABLED;

  return {
    ...settings,
    impactEnabled: enabled,
    impactSpikeThreshold: enabled
      ? Math.min(IMPACT_SPIKE_MAX, Math.max(IMPACT_SPIKE_MIN, settings.impactSpikeThreshold))
      : IMPACT_SPIKE_MIN
  };
};

// Окно неподвижности может быть дробным (например 12.5)
export const isAccStillSettings = (body: any): body is IAccStillSettings =>
  typeof body?.windowSeconds === 'number' && Number.isFinite(body.windowSeconds);

// Сервисы отдают причину ошибки в problem+json, но формат не гарантирован
export const extractDetail = (body: any): string => {
  if (!body) {
    return '';
  }
  if (typeof body === 'string') {
    return body;
  }
  return (
    [body.detail, body.message, body.title].find(
      item => typeof item === 'string' && item
    ) ?? ''
  );
};

export const describeSaveFailure = (status: number | undefined, body: any): string => {
  const detail = extractDetail(body);

  switch (status) {
    case 400:
      return detail ? `Некорректное значение: ${detail}` : 'Проверьте значения полей';
    case 502:
      return 'Сервис детекции падений вернул некорректный ответ';
    case 503:
      return 'Сервис детекции падений недоступен';
    default:
      return 'Ошибка сохранения настроек';
  }
};

export const describeAccStillSaveFailure = (status: number | undefined, body: any): string => {
  const detail = extractDetail(body);

  switch (status) {
    case 400:
      return detail ? `Некорректное значение: ${detail}` : 'Проверьте значение поля';
    case 502:
      return 'Сервис недоступен, попробуйте позже';
    case 504:
      return 'Превышено время ожидания';
    default:
      return 'Ошибка сохранения настроек';
  }
};

/**
 * 503 от BFF неоднозначна: application/json — частичное сохранение в INS
 * (в теле прежняя пара), application/problem+json — сервис недоступен.
 * Подстрока 'application/json' не входит в 'application/problem+json',
 * поэтому проверка однозначна.
 */
export const isPartialSave = (contentType: string): boolean =>
  contentType.includes('application/json');
